import React, { FunctionComponent } from "react";
import { useHistory } from "react-router-dom";
import { ElevatedStack, Grid, GridRow, GridColumn, Dropdown, SearchBox, DropdownMultiselect } from "sprint-common-web";

import { AssetType } from "../../common/types/AssetType";
import { ContentSubtype } from "../../common/types/ContentSubtype";
import { HeaderBreadcrumb } from "../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../common/ui/layout/header/PageHeader";
import { mapEnumToDropdownOptions } from "../../common/utils/mapEnumToDropdownOptions";
import { assetsSearchRoutes } from "../AssetsRoutes";
import { AncillarySearchListPage } from "./ancillary/AncillarySearchListPage";
import { getSearchAssetRouteItemByContentSubtype } from "./getSearchAssetPathByType";
import { MovieSearchListPage } from "./movie/MovieSearchListPage";
import { TvSeriesSearchListPage } from "./tv-series/TvSeriesSearchListPage";
import { useAssetSearchPageFilters } from "./useAssetSearchPageFilters";

interface AssetsSearchPageProps {
    contentType: ContentSubtype;
}

export const AssetsSearchPage: FunctionComponent<AssetsSearchPageProps> = (props: AssetsSearchPageProps) => {
    const { filters, setQuery, setTypes } = useAssetSearchPageFilters();

    const history = useHistory();

    const handleAssetTypeChange = (key?: string) => {
        const routeItem = getSearchAssetRouteItemByContentSubtype(key);
        history.push(routeItem.path);
    };

    const renderList = (selectedContentType: ContentSubtype): JSX.Element => {
        switch (selectedContentType) {
            case ContentSubtype.Movie: {
                return <MovieSearchListPage {...filters} movieContentType={ContentSubtype.Movie} />;
            }

            case ContentSubtype.TvSingle: {
                return <MovieSearchListPage {...filters} movieContentType={ContentSubtype.TvSingle} />;
            }

            case ContentSubtype.FeatureDocumentary: {
                return <MovieSearchListPage {...filters} movieContentType={ContentSubtype.FeatureDocumentary} />;
            }

            case ContentSubtype.ShortFilm: {
                return <MovieSearchListPage {...filters} movieContentType={ContentSubtype.ShortFilm} />;
            }

            case ContentSubtype.TV: {
                return <TvSeriesSearchListPage {...filters} />;
            }

            case ContentSubtype.Promo: {
                return <AncillarySearchListPage {...filters} ancillaryContentType={ContentSubtype.Promo} />;
            }

            case ContentSubtype.Safety: {
                return <AncillarySearchListPage {...filters} ancillaryContentType={ContentSubtype.Safety} />;
            }

            case ContentSubtype.Advert: {
                return <AncillarySearchListPage {...filters} ancillaryContentType={ContentSubtype.Advert} />;
            }

            case ContentSubtype.IndentWarning: {
                return <AncillarySearchListPage {...filters} ancillaryContentType={ContentSubtype.IndentWarning} />;
            }

            case ContentSubtype.ClientSpecific: {
                return <AncillarySearchListPage {...filters} ancillaryContentType={ContentSubtype.ClientSpecific} />;
            }

            default: {
                throw new Error(`'${selectedContentType}' is not content type.`);
            }
        }
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={assetsSearchRoutes} />
            </PageHeader>
            <ElevatedStack>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <Dropdown
                                options={[
                                    { key: String(ContentSubtype.Movie), text: "Movies" },
                                    { key: String(ContentSubtype.TvSingle), text: "TV Singles" },
                                    { key: String(ContentSubtype.FeatureDocumentary), text: "Feature Documentaries" },
                                    { key: String(ContentSubtype.ShortFilm), text: "Short Films" },
                                    { key: String(ContentSubtype.TV), text: "TV Series" },
                                    { key: String(ContentSubtype.Promo), text: "Promos" },
                                    { key: String(ContentSubtype.Safety), text: "Safeties" },
                                    { key: String(ContentSubtype.Advert), text: "Adverts" },
                                    { key: String(ContentSubtype.IndentWarning), text: "Indent Warnings" },
                                    { key: String(ContentSubtype.ClientSpecific), text: "Client Specific" }
                                ]}
                                testId="asset-type-dropdown"
                                selectedKey={String(props.contentType)}
                                onChange={handleAssetTypeChange}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                placeholder="Asset type"
                                selectedKeys={filters.types?.map((t) => t.toString())}
                                onChange={setTypes}
                                options={mapEnumToDropdownOptions(AssetType)}
                                testId="asset-type-multiselect"
                            />
                        </GridColumn>
                        <GridColumn sm={8}>
                            <SearchBox
                                testId="asset-searchbox"
                                placeholder="Search for..."
                                onChange={setQuery}
                                value={filters.query}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn>{renderList(props.contentType)}</GridColumn>
                    </GridRow>
                </Grid>
            </ElevatedStack>
        </>
    );
};
